'use client'

import * as React from 'react'
import { getIndexDbValue, setIndexDbValue } from 'hooks/use-index-db-value'
import { sessionStore } from 'utils/storage'

const FILE_DB_NAME = 'cf-web-tools'
const FILE_DB_VERSION = 1
const FILE_STORE_NAME = 'files'

// Storage is only read after mount so the server render and the first client render match.
export function usePersistedFormState<T>(
  key: string,
  initialValue: T,
  { restore = true }: { restore?: boolean } = {},
) {
  const [value, setValue] = React.useState<T>(initialValue)
  const [isRestored, setIsRestored] = React.useState(false)

  React.useEffect(() => {
    if (restore) {
      const stored = sessionStore.getJSON<T>(key)
      if (stored !== null) {
        setValue(stored)
      }
    }
    setIsRestored(true)
  }, [key, restore])

  React.useEffect(() => {
    if (isRestored) {
      sessionStore.setJSON(key, value)
    }
  }, [key, value, isRestored])

  return [value, setValue] as const
}

interface StoredFile {
  name: string
  type: string
  lastModified: number
  data: ArrayBuffer
}

function isStoredFile(value: unknown): value is StoredFile {
  return (
    typeof value === 'object' &&
    value !== null &&
    'name' in value &&
    typeof value.name === 'string' &&
    'data' in value &&
    value.data instanceof ArrayBuffer
  )
}

async function toStoredFile(file: File): Promise<StoredFile> {
  return {
    name: file.name,
    type: file.type,
    lastModified: file.lastModified,
    data: await file.arrayBuffer(),
  }
}

export function usePersistedFile(key: string) {
  const [file, setFileState] = React.useState<File | null>(null)

  React.useEffect(() => {
    let cancelled = false
    void getIndexDbValue<unknown>(FILE_DB_NAME, FILE_DB_VERSION, FILE_STORE_NAME, key).then(
      stored => {
        if (!cancelled && isStoredFile(stored)) {
          const restored = new File([stored.data], stored.name, {
            type: stored.type,
            lastModified: stored.lastModified,
          })
          setFileState(current => current ?? restored)
        }
      },
    )
    return () => {
      cancelled = true
    }
  }, [key])

  const setFile = React.useCallback(
    (next: File | null) => {
      setFileState(next)
      void (next ? toStoredFile(next) : Promise.resolve(null)).then(stored =>
        setIndexDbValue(FILE_DB_NAME, FILE_DB_VERSION, FILE_STORE_NAME, key, stored),
      )
    },
    [key],
  )

  return [file, setFile] as const
}

export function clearPersistedFile(key: string) {
  return setIndexDbValue(FILE_DB_NAME, FILE_DB_VERSION, FILE_STORE_NAME, key, null)
}
